import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
} from "@mui/material";
import HttpHelper from "../../utils/HttpHelper";
import ResumeDropzone from "./ResumeDropzone";
import ResumePreviewTable from "./ResumePreviewTable";

export interface ParsedResume {
    id: string;
    file: File;
    name: string;
    email: string;
    status:
    | "processing"
    | "parsed"
    | "error"
    | "created"
    | "duplicate"
    | "llm_offline";
}

interface Job {
    id: number;
    title: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
}

const ExternalResumeUploadDialog: React.FC<Props> = ({ open, onClose }) => {
    const [resumes, setResumes] = useState<ParsedResume[]>([]);
    const [jobList, setJobList] = useState<Job[]>([]);
    const [selectedJobId, setSelectedJobId] = useState<string>("");

    useEffect(() => {
        const fetchJobs = async () => {
            try {
                const response = await HttpHelper.get("jobs/all");
                setJobList(response.jobs || []);
            } catch (error) {
                console.error("Failed to fetch jobs:", error);
            }
        };

        if (open) {
            fetchJobs();
        }
    }, [open]);

    const handleFilesSelected = async (files: File[]) => {
        for (const file of files) {
            const id = crypto.randomUUID();

            setResumes((prev) => [
                ...prev,
                {
                    id,
                    file,
                    name: "",
                    email: "",
                    status: "processing",
                },
            ]);

            try {
                const formData = new FormData();
                formData.append("resume", file);

                const response = await HttpHelper.post(
                    `/hr/external-resumes`,
                    formData
                );

                setResumes((prev) =>
                    prev.map((r) =>
                        r.id === id
                            ? {
                                ...r,
                                name: `${response.parsedFirstName ?? ""} ${response.parsedLastName ?? ""}`.trim(),
                                email: response.parsedEmail ?? "",
                                status: "parsed",
                            }
                            : r
                    )
                );
            } catch (error: any) {
                let status: ParsedResume["status"] = "error";

                if (error?.errorType === "GPU_OFFLINE") {
                    status = "llm_offline";
                }

                setResumes((prev) =>
                    prev.map((r) =>
                        r.id === id
                            ? { ...r, status }
                            : r
                    )
                );
            }
        }
    };

    const handleClose = () => {
        setResumes([]);
        setSelectedJobId("");
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle fontWeight="bold">Upload External Resumes</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1, mb: 1 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="job-select-label">Select Job</InputLabel>
                        <Select
                            labelId="job-select-label"
                            value={selectedJobId}
                            label="Select Job"
                            onChange={(e) => setSelectedJobId(e.target.value as string)}
                        >
                            {jobList.map((job) => (
                                <MenuItem key={job.id} value={job.id.toString()}>
                                    {job.title}
                                </MenuItem>
                            ))}
                        </Select>
                        {!selectedJobId && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                Please select a job before creating applicants
                            </Typography>
                        )}
                    </FormControl>
                </Box>

                <ResumeDropzone onFilesSelected={handleFilesSelected} />

                {resumes.length > 0 && (
                    <ResumePreviewTable
                        resumes={resumes}
                        setResumes={setResumes}
                        jobIdProp={selectedJobId}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExternalResumeUploadDialog;
